import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FreestyleCombobox } from '@/components/ui/freestyle-combobox';
import { Combobox } from '@/components/ui/combobox';
import { DatePicker } from '@/components/ui/date-picker';
import { useExpenseCategories } from '@/hooks/useExpenseCategories';
import { useTopVendors } from '@/hooks/useTopVendors';
import { useTopDescriptions } from '@/hooks/useTopDescriptions';
import { todayLocalISO, dateToLocalISO } from '@/lib/format';
import { expenseProjectName } from '@jobos/shared';
import type { ExpenseWithDetails, ExpensePaymentMethod } from '@jobos/shared';

const paymentMethodSchema = z.enum(['check', 'credit_card', 'cash']);

const expenseFormSchema = z
  .object({
    storeName: z.string().min(1, 'Store name is required'),
    description: z.string().min(1, 'Description is required'),
    amount: z.string().min(1, 'Amount is required').refine(
      // Negative amounts are allowed — they represent refunds / returned materials.
      (val) => val.trim() !== '' && !isNaN(Number(val)),
      'Must be a number'
    ),
    expenseDate: z.string().min(1, 'Date is required'),
    categoryId: z.string().uuid('Category is required'),
    // The project is carried by name, not id — breakdown project ids belong to
    // one breakdown document and are re-minted whenever it is copied.
    projectName: z.string().optional(),
    paidDate: z.string(),
    paymentMethod: paymentMethodSchema.nullable(),
    checkNumber: z.string(),
    reimburse: z.boolean(),
  })
  .refine(
    (data) => data.paymentMethod !== 'check' || data.checkNumber.trim().length > 0,
    { path: ['checkNumber'], message: 'Check # is required' }
  );

export type ExpenseFormData = z.infer<typeof expenseFormSchema>;

interface ExpenseFormProps {
  projects?: { id: string; name: string }[];
  editingExpense?: ExpenseWithDetails | null;
  onSubmit: (data: ExpenseFormData) => void;
  isPending?: boolean;
  onCancel: () => void;
  /** Hide the footer Cancel when a higher-level Cancel (the batch stepper) is shown. */
  showCancel?: boolean;
  onBack?: () => void;
  /** Drop the current receipt from the batch without saving it. */
  onSkip?: () => void;
  /** Advance to the next batch item without saving (revisited, unchanged item). */
  onAdvance?: () => void;
  /** Present only in the multi-receipt batch flow; drives the submit-button label. */
  batchPosition?: { isSaved: boolean; isLast: boolean };
}

const PAYMENT_METHOD_OPTIONS: { value: ExpensePaymentMethod; label: string }[] = [
  { value: 'check', label: 'Check' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'cash', label: 'Cash' },
];

export function ExpenseForm({
  projects,
  editingExpense,
  onSubmit,
  isPending,
  onCancel,
  showCancel = true,
  onBack,
  onSkip,
  onAdvance,
  batchPosition,
}: ExpenseFormProps) {
  const { data: categories } = useExpenseCategories();
  const { data: topVendors } = useTopVendors();
  const { data: topDescriptions } = useTopDescriptions();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ExpenseFormData>({
    resolver: zodResolver(expenseFormSchema),
    defaultValues: {
      storeName: editingExpense?.storeName ?? '',
      description: editingExpense?.description ?? '',
      amount: editingExpense ? String(editingExpense.amount) : '',
      expenseDate: editingExpense?.expenseDate ?? todayLocalISO(),
      categoryId: editingExpense?.categoryId ?? '',
      projectName: (editingExpense ? expenseProjectName(editingExpense) : null) ?? '',
      paidDate: editingExpense?.paidDate ?? '',
      paymentMethod: editingExpense?.paymentMethod ?? null,
      checkNumber: editingExpense?.checkNumber ?? '',
      reimburse: editingExpense?.reimburse ?? false,
    },
  });

  // Category picker is a type-to-filter combobox; options sorted A→Z regardless
  // of the user's custom Settings sort order.
  const categoryOptions = [...(categories ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((cat) => ({ value: cat.id, label: cat.name }));

  // Options are the active breakdown's project names. An expense saved against
  // a project that has since been renamed or removed keeps its own name in the
  // list, so opening the edit form doesn't silently reassign it to "No project".
  const savedProjectName = editingExpense ? expenseProjectName(editingExpense) : null;
  const projectOptions = (projects ?? []).map((proj) => proj.name);
  if (savedProjectName && !projectOptions.includes(savedProjectName)) {
    projectOptions.push(savedProjectName);
  }

  const paymentMethod = watch('paymentMethod');
  // Reimburse means "still owed to whoever paid" — mutually exclusive with a paid date.
  const reimburse = watch('reimburse');
  const reimburseField = register('reimburse');
  // A negative amount is a refund / return — flag it green so it's distinct from a charge.
  const amountValue = watch('amount');
  const isRefund = amountValue !== '' && !isNaN(Number(amountValue)) && Number(amountValue) < 0;

  // Submit-button text. In the batch flow: a fresh receipt "saves", a revisited
  // saved receipt "updates" only if the user actually changed something —
  // otherwise it's a plain forward "Next"/"Finish".
  let submitText: string;
  // When a revisited saved item is left untouched, the button just moves on
  // (no redundant update / toast) — handled via onAdvance instead of submit.
  const advanceOnly = !!batchPosition?.isSaved && !isDirty;
  if (!batchPosition) {
    submitText = editingExpense ? 'Update Expense' : 'Add Expense';
  } else {
    const tail = batchPosition.isLast ? 'Finish' : 'Next →';
    if (!batchPosition.isSaved) {
      submitText = `Save & ${tail}`;
    } else if (isDirty) {
      submitText = `Update & ${tail}`;
    } else {
      submitText = tail;
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full">
      <div className="flex-1 space-y-4 p-4 sm:p-5">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Store / Vendor</label>
          <Controller
            name="storeName"
            control={control}
            render={({ field }) => (
              <FreestyleCombobox
                value={field.value}
                onValueChange={field.onChange}
                options={(topVendors ?? []).map((v) => ({ value: v, label: v }))}
                placeholder="e.g. Home Depot"
                searchPlaceholder="Search or type vendor..."
                emptyText="No vendors yet. Type one to add it."
              />
            )}
          />
          {errors.storeName && <p className="text-sm text-red-600 mt-1">{errors.storeName.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Amount</label>
            <Input
              {...register('amount')}
              type="number"
              step="0.01"
              // iOS picks the keyboard off this rather than the type, and the
              // decimal pad is the one with a point on it.
              inputMode="decimal"
              placeholder="0.00"
              className={isRefund ? 'text-green-600' : undefined}
            />
            {isRefund ? (
              <p className="text-xs text-green-600 mt-1">Refund / return (negative)</p>
            ) : (
              errors.amount && <p className="text-sm text-red-600 mt-1">{errors.amount.message}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Date</label>
            <Controller
              name="expenseDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value || null}
                  onChange={(date) => field.onChange(date ? dateToLocalISO(date) : '')}
                  placeholder="MM/DD/YYYY"
                />
              )}
            />
            {errors.expenseDate && <p className="text-sm text-red-600 mt-1">{errors.expenseDate.message}</p>}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Category</label>
          <Controller
            name="categoryId"
            control={control}
            render={({ field }) => (
              <Combobox
                value={field.value ?? ''}
                onValueChange={field.onChange}
                options={categoryOptions}
                placeholder="Select category..."
                searchPlaceholder="Search categories..."
                emptyText="No categories found."
              />
            )}
          />
          {errors.categoryId && <p className="text-sm text-red-600 mt-1">{errors.categoryId.message}</p>}
        </div>

        {projects && projects.length > 0 && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Project</label>
            {/* 16px below `md` or iOS zooms the page in on focus and leaves it
                there — the same reason `Input` carries `text-base md:text-sm`. */}
            <select
              {...register('projectName')}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-base md:text-sm"
            >
              <option value="">No project</option>
              {projectOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Description</label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <FreestyleCombobox
                value={field.value}
                onValueChange={field.onChange}
                options={(topDescriptions ?? []).map((d) => ({ value: d, label: d }))}
                placeholder="What was purchased"
                searchPlaceholder="Search or type description..."
                emptyText="No descriptions yet. Type one to add it."
              />
            )}
          />
          {errors.description && <p className="text-sm text-red-600 mt-1">{errors.description.message}</p>}
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Payment Method</label>
          <Controller
            control={control}
            name="paymentMethod"
            render={({ field }) => (
              // Three segments at their natural width overflow 390px; below `sm`
              // they share the row instead, which also makes each one a target
              // a thumb can hit.
              <div className="flex w-full overflow-hidden rounded-md border border-gray-300 text-sm sm:inline-flex sm:w-auto">
                {PAYMENT_METHOD_OPTIONS.map((opt) => {
                  const active = field.value === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`flex-1 whitespace-nowrap px-3 py-2.5 sm:flex-none sm:py-1.5 ${
                        active ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        field.onChange(active ? null : opt.value);
                        if (opt.value !== 'check') {
                          setValue('checkNumber', '');
                        }
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          />
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-8">
          <div>
            <label className={`block text-xs font-semibold mb-1 uppercase ${reimburse ? 'text-gray-300' : 'text-gray-500'}`}>Paid Date</label>
            <Controller
              name="paidDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value || null}
                  onChange={(date) => field.onChange(date ? dateToLocalISO(date) : '')}
                  placeholder="MM/DD/YYYY"
                  disabled={reimburse}
                />
              )}
            />
          </div>

          <label
            htmlFor="reimburse"
            className="flex items-center gap-2 h-9 text-sm text-gray-700 cursor-pointer select-none"
          >
            <input
              type="checkbox"
              {...reimburseField}
              onChange={(e) => {
                reimburseField.onChange(e);
                // Toggling on "needs reimbursement" clears any paid date — can't be both.
                if (e.target.checked) setValue('paidDate', '');
              }}
              id="reimburse"
              className="h-4 w-4 rounded border-gray-300"
            />
            Reimburse
          </label>
        </div>

        {paymentMethod === 'check' && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Check #</label>
            <Input {...register('checkNumber')} inputMode="numeric" placeholder="e.g. 1042" />
            {errors.checkNumber && <p className="text-sm text-red-600 mt-1">{errors.checkNumber.message}</p>}
          </div>
        )}
      </div>

      {/* Sticky on a phone: a batch of five is five trips down a form that is
          taller than the screen, and the button that ends each trip should not
          be one of the things you have to scroll to find. */}
      <div className="sticky bottom-0 flex items-center gap-2 border-t bg-white p-4 sm:static sm:p-5 sm:pt-3">
        {onBack && (
          <Button type="button" variant="ghost" onClick={onBack} className="min-h-[44px] px-3 sm:min-h-0">
            ← Back
          </Button>
        )}
        {onSkip && (
          <Button
            type="button"
            variant="ghost"
            onClick={onSkip}
            disabled={isPending}
            className="min-h-[44px] px-3 text-gray-500 sm:min-h-0"
          >
            Skip →
          </Button>
        )}
        <div className="ml-auto flex gap-2">
          {showCancel && (
            <Button type="button" variant="outline" onClick={onCancel} className="min-h-[44px] sm:min-h-0">
              Cancel
            </Button>
          )}
          {advanceOnly && onAdvance ? (
            <Button type="button" onClick={onAdvance} className="min-h-[44px] sm:min-h-0">
              {submitText}
            </Button>
          ) : (
            <Button type="submit" disabled={isPending} className="min-h-[44px] sm:min-h-0">
              {isPending ? 'Saving...' : submitText}
            </Button>
          )}
        </div>
      </div>
    </form>
  );
}
